import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { startOfWeekMonIso, todayLocalIso, fmtDate, dayOfWeekIso, addDaysIso } from '../../shared/dates';
import { ScheduleGrid, type Block } from './ScheduleGrid';
import { ServiceLabels } from '@census/shared';

export function PrintProviderWeekPage() {
  const { id } = useParams<{ id: string }>();
  const weekStart = startOfWeekMonIso(todayLocalIso());

  const { data } = useQuery({
    queryKey: ['provider-week', id, weekStart],
    queryFn: () => api.get(`/schedule/provider/${id}`, { params: { weekStart } }).then((r) => r.data),
    enabled: !!id,
  });
  const { data: settings } = useQuery({
    queryKey: ['clinic-info'],
    queryFn: () => api.get('/settings/clinic_info').then((r) => r.data),
  });

  useEffect(() => {
    if (data) setTimeout(() => window.print(), 350);
  }, [data]);

  if (!data) return <div className="p-6 text-sm">Loading…</div>;

  const blocks: Block[] = (data.instances ?? []).map((i: any) => ({
    day: dayOfWeekIso(i.scheduledDate),
    startMin: i.startMin,
    endMin: i.endMin,
    label: i.groupName ?? ServiceLabels[i.serviceType as 'THERAPY'],
    sub: i.kind === 'GROUP' ? `Group · ${i.cohortId ?? ''}` : `${i.cohortId} ${i.clientName}`,
    kind: i.kind,
  }));

  return (
    <div className="min-h-screen bg-white p-6">
      <header className="mb-4 flex items-start justify-between border-b border-slate-300 pb-3">
        <div>
          <div className="text-base font-semibold text-slate-900">{settings?.clinicName ?? 'Clinic'}</div>
          <div className="text-xs text-slate-500">{settings?.clinicAddress}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">Provider Week</div>
          <div className="text-xs text-slate-500">
            Week of {fmtDate(weekStart)} → {fmtDate(addDaysIso(weekStart, 6))}
          </div>
        </div>
      </header>

      <section className="mb-3">
        <h1 className="text-xl font-semibold">{data.resource.displayName}</h1>
        <p className="text-sm text-slate-600">
          {data.resource.providerRole ?? data.resource.type}
          {data.weeklyCapMinutes > 0 && (
            <>
              {' · '}
              {(data.weeklyMinutes / 60).toFixed(1)}h / {(data.weeklyCapMinutes / 60).toFixed(0)}h cap ·{' '}
              {data.utilizationPct.toFixed(0)}% util
            </>
          )}
          {' · '}
          {data.uniqueClients} unique clients
        </p>
      </section>

      <ScheduleGrid blocks={blocks} />

      <section className="mt-8 text-xs">
        <h3 className="text-sm font-semibold text-slate-700">Provider signature</h3>
        <span className="inline-block w-72 border-b border-slate-400" /> Date: <span className="inline-block w-24 border-b border-slate-400" />
      </section>
    </div>
  );
}
